import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Waves, Feather, ShieldCheck, Facebook, ArrowRight } from "lucide-react";

export default function Home() {
  const [huts, setHuts] = useState([]);
  const [content, setContent] = useState({});
  useEffect(() => {
    api.get("/huts").then(r => setHuts(r.data));
    api.get("/content").then(r => setContent(r.data.content || {}));
  }, []);
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage:"url('https://images.unsplash.com/photo-1760891680309-9969f285a823?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600')"}} />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/70 via-emerald-900/50 to-[#FAF8F5]" />
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28 text-white">
          <span className="inline-block text-xs uppercase tracking-widest bg-white/10 backdrop-blur px-3 py-1 rounded-full border border-white/20">Association loi 1901 · Somme</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 max-w-3xl leading-tight">Découvrez les huttes pédagogiques en vallée de Somme à Mareuil-Caubert</h1>
          <p className="mt-4 text-lg text-stone-100/90 max-w-2xl">Restauration, entretien et préservation des zones humides — l'AGCVS ouvre à la réservation les nuits disponibles dans ses huttes découverte.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/reserver" className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2" data-testid="hero-book-btn">Réserver une nuit <ArrowRight size={18}/></Link>
            <Link to="/huttes" className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white px-6 py-3 rounded-md font-semibold" data-testid="hero-huts-btn">Découvrir les huttes</Link>
          </div>
        </div>
      </section>

      {/* Presentation */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <h2 className="font-serif text-3xl text-emerald-900 mb-6" data-testid="presentation-title">Présentation de l'AGCVS</h2>
        <div className="prose max-w-none text-stone-700 whitespace-pre-line leading-relaxed" data-testid="presentation-text">
          {content.presentation || "Bienvenue sur le site de l'AGCVS."}
        </div>
        {content.marsh_photo_url && (
          <figure className="mt-8">
            <img src={fileUrl(content.marsh_photo_url)} alt="Vue aérienne du marais de Mareuil-Caubert avec les deux huttes signalées en rouge" className="w-full rounded-xl border border-stone-200" data-testid="marsh-aerial-photo"/>
            <figcaption className="mt-2 text-sm text-stone-500 text-center">Vue aérienne du marais de Mareuil-Caubert — les deux huttes pédagogiques sont signalées en rouge.</figcaption>
          </figure>
        )}
      </section>

      {/* Values */}
      <section className="bg-stone-50 py-14 border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-4 grid gap-6 md:grid-cols-3">
          <ValueCard icon={<Waves className="text-emerald-800" />} title="Restaurer les zones humides" text="Réouverture et entretien des marais de la vallée de Somme, notamment à Mareuil-Caubert." />
          <ValueCard icon={<Feather className="text-emerald-800" />} title="Transmettre & découvrir" text="Faire découvrir aux non-chasseurs et jeunes chasseurs les marais et le gibier d'eau." />
          <ValueCard icon={<ShieldCheck className="text-emerald-800" />} title="Réserver simplement" text="Deux huttes pédagogiques, réservation en ligne sécurisée, 40 € la nuit." />
        </div>
      </section>

      {/* Huts preview */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="font-serif text-3xl text-emerald-900 mb-8" data-testid="huts-title">Nos huttes pédagogiques</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {huts.map(h => (
            <Link key={h.id} to={`/huttes/${h.slug}`} className="group block bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-lg transition" data-testid={`hut-card-${h.slug}`}>
              <div className="aspect-[4/3] bg-stone-200 overflow-hidden">
                <img src={h.photos?.[0] ? fileUrl(h.photos[0]) : ""} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl text-emerald-900">{h.name}</h3>
                  {h.status !== "ACTIVE" && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">Travaux en cours</span>}
                </div>
                <div className="mt-2 text-sm text-stone-600">Capacité : {h.max_capacity} personnes · {h.beds} couchages</div>
                <div className="mt-3 text-emerald-800 font-semibold">{h.price_per_night} € / nuit</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Facebook */}
      <section className="bg-emerald-50 border-t border-emerald-100 py-10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <a href={content.facebook_url || "https://www.facebook.com/share/g/1DEc3um79M/"} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-emerald-800 hover:text-emerald-900 font-semibold" data-testid="fb-link">
            <Facebook /> Retrouvez davantage de photos sur notre groupe Facebook
          </a>
        </div>
      </section>
    </div>
  );
}

function ValueCard({ icon, title, text }) {
  return (
    <div className="bg-white p-6 rounded-xl border border-stone-200">
      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-4">{icon}</div>
      <h3 className="font-serif text-lg text-emerald-900 mb-2">{title}</h3>
      <p className="text-sm text-stone-600">{text}</p>
    </div>
  );
}
